import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useCallback, useEffect, useState } from "react";
import { useContractAnalysis } from "src/hooks/contracts/useContractAnalysis";
import type { ContractAnalysisProps, GetContractResponseContent } from "src/types/contract.types";
import { TopNavigation } from "./components/top-navigation";
import { ContractAnalysisLoadingView } from "./components/loading";
import { ActivitySidebar } from "./components/activity-sidebar";
import { ActivitySidebarProvider, useSidebarContext } from "./contexts/activity-sidebar-context";
import { ContractEditor } from "./contract-editor";
import { Panel, Group, Separator } from "react-resizable-panels";

interface ContractAnalysisComponentProps extends ContractAnalysisProps {
  isDemo?: boolean;
  hideNavigation?: boolean;
}

type ViewMode = "edit" | "viewer";

function ContractAnalysisContent({
  contract,
  hideNavigation,
  viewMode,
  onViewModeChange,
}: {
  contract: GetContractResponseContent | null;
  hideNavigation: boolean;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}) {
  const { sidebarOpen, openSidebar, setSidebarTab } = useSidebarContext();
  const [iqHighlightedTerm, setIqHighlightedTerm] = useState<string | null>(null)
  const sourceTaggedText = contract?.contractExtraction?.contractTexts?.taggedText?.text ?? ""
  const [taggedContractText, setTaggedContractText] = useState(sourceTaggedText)

  useEffect(() => {
    setTaggedContractText(sourceTaggedText)
  }, [contract?.contractId, sourceTaggedText])

  const handleViewerTermSelect = useCallback((termWithInstance: string) => {
    setIqHighlightedTerm(termWithInstance)
    openSidebar()
    setSidebarTab("citations")
  }, [openSidebar, setSidebarTab])

  const handleCitationTermSelect = useCallback((termWithInstance: string) => {
    setIqHighlightedTerm(termWithInstance)
  }, [])

  const handleTaggedContractTextChange = useCallback((nextText: string) => {
    setTaggedContractText(nextText)
  }, [])

  const handleIqTermSelect = useCallback((termWithInstance: string) => {
    setIqHighlightedTerm(termWithInstance)
    if (viewMode === "edit") {
      onViewModeChange("viewer")
    }
  }, [onViewModeChange, viewMode])

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {!hideNavigation && (
        <TopNavigation
          contract={contract}
        />
      )}

      <Group orientation="horizontal" className="flex-1 min-h-0">
        <Panel defaultSize={70} minSize={40}>
          <div className={sidebarOpen ? "h-full overflow-hidden border-r" : "h-full overflow-hidden"}>
            <ContractEditor
              mode={viewMode}
              onModeChange={onViewModeChange}
              contract={contract}
              highlightedTerm={iqHighlightedTerm}
              onHighlightedTermSelect={handleViewerTermSelect}
              taggedContractText={taggedContractText}
              onTaggedContractTextChange={handleTaggedContractTextChange}
            />
          </div>
        </Panel>
        {sidebarOpen && (
          <>
            <Separator className="w-1 bg-border/50 hover:bg-border transition-colors cursor-col-resize" />
            <Panel defaultSize={50} minSize={20}>
              <div className="sticky top-0 h-[calc(100vh-4rem)]">
                <ActivitySidebar
                  contract={contract ?? undefined}
                  onIqTermSelect={handleIqTermSelect}
                  highlightedTerm={iqHighlightedTerm}
                  onCitationTermSelect={handleCitationTermSelect}
                  taggedContractText={taggedContractText}
                />
              </div>
            </Panel>
          </>
        )}
      </Group>
    </div>
  );
}

const ContractAnalysis = ({ providedContract, isDemo = false, hideNavigation = false }: ContractAnalysisComponentProps) => {
  const {
    contract,
    isLoading,
  } = useContractAnalysis(providedContract, isDemo);

  const [viewMode, setViewMode] = useState<ViewMode>("viewer");

  if (isLoading) {
    return <ContractAnalysisLoadingView />
  }

  return (
    <ActivitySidebarProvider>
      <ContractAnalysisContent
        contract={contract}
        hideNavigation={hideNavigation}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
    </ActivitySidebarProvider>
  );
}

export default ContractAnalysis;
